import React, { useState } from 'react';
import { Search, ShoppingCart, User, Smartphone, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { cart } = useCart();
  const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <header className="bg-white border-b border-stone-200 sticky top-0 z-50" data-testid="header">
      {/* Top Kilim Pattern Bar */}
      <div className="h-1"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, 
            #78350f 0px, #78350f 30px, 
            #dc2626 30px, #dc2626 60px, 
            #f59e0b 60px, #f59e0b 90px)`
        }}
      ></div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <div className="text-2xl font-bold leading-tight">
              <div className="flex items-center gap-1">
                <span className="text-amber-600 text-3xl font-black">A</span>
                <span className="text-stone-900 text-2xl font-bold">mlaria</span>
              </div>
            </div>
          </Link>

          {/* Search Bar */}
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search phone covers & accessories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-12 py-2 h-12 rounded-none border-stone-300 bg-stone-50 focus:ring-amber-500 focus:border-amber-500 text-sm"
                data-testid="search-input"
              />
              <Button
                className="absolute right-1 top-1/2 -translate-y-1/2 bg-amber-600 hover:bg-amber-700 rounded-none h-10 w-10 p-0"
                data-testid="search-btn"
              >
                <Search className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Country Flag */}
            <div className="hidden lg:flex items-center">
              <img
                src="https://flagcdn.com/w40/us.png"
                alt="US Flag"
                className="w-8 h-6 rounded"
              />
            </div>

            {/* Lounge Link */}
            <Link
              to="/lounge"
              className="hidden lg:flex flex-col items-center text-stone-700 hover:text-amber-600 transition-colors group"
              data-testid="lounge-link"
            >
              <div className="w-10 h-10 bg-amber-100 group-hover:bg-amber-200 flex items-center justify-center mb-1 transition-colors">
                <Sparkles className="h-5 w-5 text-amber-700" />
              </div>
              <span className="text-xs whitespace-nowrap font-medium">Lounge</span>
            </Link>

            {/* My Account */}
            <Link
              to="/account"
              className="hidden lg:flex flex-col items-center text-stone-700 hover:text-amber-600 transition-colors"
              data-testid="account-link"
            >
              <User className="h-8 w-8 mb-1" />
              <span className="text-xs whitespace-nowrap">My Account</span>
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative flex items-center gap-2 bg-stone-100 hover:bg-amber-100 transition-colors px-4 py-3 border border-stone-200 hover:border-amber-300"
              data-testid="cart-link"
            >
              <div className="relative">
                <ShoppingCart className="h-6 w-6 text-stone-700" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                    {itemCount}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-stone-600">Cart</span>
                <span className="text-sm font-semibold text-stone-800">
                  ${(cart.total || 0).toFixed(2)}
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
